<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import SalesDetailFactList from './SalesDetailFactList.vue';
import {
  genderOptions,
  marriageRegimeOptions,
  maritalStatusOptions,
} from '../composables/salesPartyFormOptions';

const props = defineProps({ manager: { type: Object, required: true } });
const { t } = useI18n();
const identity = computed(() => props.manager.profile?.identity || {});
const optionLabel = (options, value, fallback) =>
  options.find(option => option.value === String(value))?.label ||
  fallback ||
  value;

const items = computed(() => {
  const values = identity.value;
  const marriageRegime =
    values.marital_property_regime_label ||
    optionLabel(marriageRegimeOptions(t), values.marital_property_regime);
  const shouldShowMarriageRegime =
    String(values.marital_property_regime_registered) === '1' ||
    Boolean(
      values.marital_property_regime && values.marital_property_regime !== '0'
    );

  return [
    { key: 'cpf', label: t('SALES_PARTIES.FORM.CPF'), value: values.cpf },
    {
      key: 'birth-date',
      label: t('SALES_PARTIES.FORM.BIRTH_DATE'),
      value: values.birth_date,
    },
    {
      key: 'gender',
      label: t('SALES_PARTIES.FORM.GENDER'),
      value: optionLabel(genderOptions(t), values.gender, values.gender_label),
    },
    { key: 'name', label: t('SALES_PARTIES.FORM.NAME'), value: values.name },
    {
      key: 'mother-name',
      label: t('SALES_PARTIES.FORM.MOTHER_NAME'),
      value: values.mother_name,
    },
    {
      key: 'father-name',
      label: t('SALES_PARTIES.FORM.FATHER_NAME'),
      value: values.father_name,
    },
    {
      key: 'nationality',
      label: t('SALES_PARTIES.FORM.NATIONALITY'),
      value: values.nationality,
    },
    { key: 'phone', label: t('SALES_PARTIES.FORM.PHONE'), value: values.phone },
    {
      key: 'profession',
      label: t('SALES_PARTIES.FORM.PROFESSION'),
      value: values.profession,
    },
    { key: 'email', label: t('SALES_PARTIES.FORM.EMAIL'), value: values.email },
    { key: 'rg', label: t('SALES_PARTIES.FORM.RG'), value: values.rg },
    {
      key: 'rg-issuer',
      label: t('SALES_PARTIES.FORM.RG_ISSUER'),
      value: values.rg_issuer,
    },
    {
      key: 'rg-issued-on',
      label: t('SALES_PARTIES.FORM.RG_ISSUED_ON'),
      value: values.rg_issued_on,
    },
    {
      key: 'marital-status',
      label: t('SALES_PARTIES.FORM.MARITAL_STATUS'),
      value: optionLabel(
        maritalStatusOptions(t),
        values.marital_status,
        values.marital_status_label
      ),
    },
    values.spouse_name && {
      key: 'spouse-name',
      label: t('SALES_PARTIES.FORM.SPOUSE_NAME'),
      value: values.spouse_name,
    },
    shouldShowMarriageRegime && {
      key: 'marital-property-regime',
      label: t('SALES_PARTIES.FORM.MARRIAGE_REGIME'),
      value: marriageRegime,
    },
  ].filter(Boolean);
});
</script>

<template>
  <LaisCard
    :title="t('SALES.DETAIL.MANAGER_PERSONAL.TITLE')"
    prefix-icon="i-lucide-user-round"
  >
    <SalesDetailFactList :items="items" />
  </LaisCard>
</template>
